/**
 * @file Controller for a 2D game
 * @author Gonzalo Alvarez de Aldecoa <gon@galdecoa.com>
 * @license GPL-3.0-or-later
 * @version 2404.04.001
 */

/** @module game */

import { Box } from "./Box.js";
import { Sprite } from "./Sprite.js";
import { Interactive } from "./Interactive.js";

/** 
 * A representation of a 2D game. 
 * @class
 */
class Game {
	
	static CollisionFlag = Object.freeze( Object.create( null, {
		"NONE":     { value: 0 },
		"OBSTACLE": { value: 1 },
		"SPRITE":   { value: 2 },
		"PLAYER":   { value: 4 }
	} ) );

	/** 
	 * Minimum dimensions of the canvas.
	 * The canvas element's width and height properties may differ from its visual 
	 * dimensions, in the same way an `img` element may have a width and a height
	 * but be styled/rendered with different values.
	 * @see {@link module:game~Game#canvasDistortion canvasDistortion}
	 * @type {Object} 
	 * @default { width: 640, height: 480 }
	 * */
	#minCanvasDimensions = { width: 640, height: 480 };
	/** 
	 * The `{@link module:game~Game#canvas canvas}` element to render the game on.
	 * @type {HTMLCanvasElement?} 
	 * @default null
	 */
	#canvas = null;
	/** 
	 * The `{@link module:game~Game#canvas canvas}` width property value.
	 * @type {Number} 
	 */
	#canvasWidth = this.#minCanvasDimensions.width * 1;
	/** 
	 * The `{@link module:game~Game#canvas canvas}` height property value.
	 * @type {Number} 
	 */
	#canvasHeight = this.#minCanvasDimensions.height * 1;
	/** 
	 * The proportion between the `{@link module:game~Game#canvas canvas}`' width 
	 * and height properties and its values. 
	 * The canvas element's width and height properties may differ from its visual 
	 * dimensions, in the same way an `img` element may have a width and a height
	 * but be styled/rendered with different values.
	 * @see {@link module:game~Game#minCanvasDimensions minCanvasDimensions}
	 * @type {Object} 
	 * @default { x: 1, y: 1 }
	 */
	#canvasDistortion = { x: 1, y: 1 };
	/** 
	 * The bounding client rectangle of the `{@link module:game~Game#canvas canvas}`.
	 * @type {DOMRect?} 
	 * @default null
	 */
	#boundingClientRect = null;
	/** 
	 * The 2d rendering context of the `{@link module:game~Game#canvas canvas}`.
	 * @type {CanvasRenderingContext2D?} 
	 * @default null
	 */
	#context = null;

	/** 
	 * The size of a game tile, in pixels.
	 * @type    {Object} 
	 * @default { width: 64, height: 64 }
	 */
	#tileSize = { width: 64, height: 64 };

	#screens = {};
	#images = {};

	get images() { return this.#images; }

	/** 
	 * An array of areas in which to render area.
	 * @type {Object[]} 
	 */
	#waterBodies = [];
	/** 
	 * An pattern to render water with.
	 * @type {Image?} 
	 * @see {@link module:game~Game#waterPattern waterPattern}
	 */
	#waterTileImage = null;
	/** 
	 * A `CanvasPattern` to render water with.
	 * @type {CanvasPattern} 
	 * @see {@link module:game~Game#waterTileImage waterTileImage}
	 */
	#waterPattern = null;
	/** @type {Object[]} */
	#waterBodiesUV = [];
	/** @type {Image?} */
	#backgroundImage = null;
	/** @type {Image?} */
	#foregroundImage = null;

	/** @type {Boolean} */
	#isPaused = true;

	/** @type {Object} */
	#controls = { UP: "w", LEFT: "a", DOWN: "s", RIGHT: "d", INTERACT: "i"	};
	/** @type {Set} */
	#pressedKeys = new Set();
	/** @type {String?} */
	#activeKey = null;

	/** @type {Object} */
	#defaultSpriteOptions = { 
		isPlayer: false, 
		alwaysOnTop: false,
		canMove: false
	};
	/** @type {Sprite[]} */
	#sprites = [];
	/** @type {Sprite?} */
	#playerSprite = null;
	/** @type {Sprite[]} */
	#spritesOnTop = [];

	/** @type {Object} */
	#cameraOffset = { x: 0, y : 0 };

	/** @type {Number} */
	#movementSpeed = 1.5;

	/** @type {Number} */
	#playerSpeedFactor = 1;

	/** @type {Object[]} */
	#obstacles = [];
	/** @type {Number} */
	#lastObstacleIdx = 0;

	/** @type {Boolean} */
	#showCollisions = false;
	/** @type {Boolean} */
	#showGrid = false;

	/** @type {Number} */
	#fps = 10;
	/** @type {Number} */
	#animationInterval = 1000 / this.#fps;
	/** @type {Number} */
	#lastTimestamp = 0;
	/** @type {Number} */
	#animationTimeSpan = 0;
	/** @type {Number} */
	#currentFrame = -1;

	/** @type {Object[]} */
	#interactives = [];
	/** @type {Set} */
	#touchedInteractivesIdx = new Set();
	/** @type {Set} */
	#remainingInteractivesIdx = new Set();
	/** @type {Number} */
	#lastInteractiveIdx = 0;
	/** @type {Box[]} */
	#interactiveBoxes = [];
	/** @type {Boolean} */
	#isHintingInteraction = false;

	/** @type {Object[]} */
	#insectAreas = [];

	/** @type {Object[]} */
	#movingSpritesStates = [];

	/** @type {String} */
	#pointerKeyEquivalent = "";
	/** @type {Boolean} */
	#isPressingMouseButton = false;

	/** @type {Boolean} */
	#isUsingTouch = false;

	/** @type {Object?} */
	#screenTransition = null;

	#isInTransition = false;

	/**
	 * Load a set of images.
	 * @param   {Object}  imageURLs 
	 * 					An object where each property is an image URL.
	 * @returns {Object}
	 *          An object where each property is an `Image`.
	 */
	static async imagePreloader( imageURLs ) {
		const images = { };
		const imagePromises = [];
		for( let imageKey in imageURLs ) {
			const imageSRC = imageURLs[ imageKey ];
			imagePromises.push( new Promise( ( resolve, reject ) => {
				let image = new Image();
				image.onload = resolve;
				image.onerror = reject;
				image.src = imageSRC;
				images[ imageKey ] = image;
			} ) );
		}
		await Promise.allSettled( imagePromises );
		return images;
	}

	/**
	 * Load a set of JSON files.
	 * @param   {Object}  jSONURLs 
	 * 					An object where each property is a JSON file URL.
	 * @returns {Object}
	 *          An object where each property is an object.
	 */
	static async jSONPreloader( jSONURLs ) {
		const data = { };
		const jsonPromises = [];
		for( let jSONKey in jSONURLs ) {
			const jsonURL = jSONURLs[ jSONKey ];
			jsonPromises.push( 
				fetch( jsonURL )
					.then( response => response.json() ) 
					.then( object => data[jSONKey] = object )
			);
		}
		await Promise.allSettled( jsonPromises );
		return data;
	}

	/**
	 * Load a set of XML files.
	 * @param   {Object}  jMLURLs 
	 * 					An object where each property is a XML file URL.
	 * @returns {Object}
	 *          An object where each property is an XMLDocument.
	 */
	static async xMLPreloader ( xMLURLs ) {
		const data = { };
		const xMLPromises = [];
		const parser = new DOMParser();
		const type = "application/xml";
		for( let xMLKey in xMLURLs ) {
			const xMLURL = xMLURLs[ xMLKey ];
			xMLPromises.push( 
				fetch( xMLURL )
					.then( response => response.text() ) 
					.then( xML => data[xMLKey] = parser.parseFromString( xML, type ) )
			);
		}
		await Promise.allSettled( xMLPromises );
		return data;
	}

	static checkFlags( flags, flag ) { return ( ( flags & flag ) === flag ); }

	static #debounce( func, timeout = 300 ){
		let timer;
		return ( ...args ) => {
			clearTimeout( timer );
			timer = setTimeout( () => { func.apply( this, args ); }, timeout );
		};
	}

	/**
	 * Linear intERPolation.
	 * @param {Number} min 
	 * @param {Number} max 
	 * @param {Number} perunit 
	 * @returns {Number} min + perunit * ( max - min )
	 */
	static #lerp( min, max, perunit ) { return min + perunit * ( max - min ); }

	/**
	 * The `canvas` element where the game is rendered.
	 * @memberof module:game~Game
	 * @readonly
	 * @type {HTMLCanvasElement}
	 */
	get canvas() { return this.#canvas; }

	get screenTransition() { return this.#screenTransition; }
	set screenTransition( value ) { this.#screenTransition = value; }

	#setWaterTile( image ) {
		if ( !image ) {
			this.#waterTileImage = null;
			this.#waterPattern = null;
			return;
		}
		this.#waterTileImage = image;
		this.#waterPattern = this.#context.createPattern( image, "repeat" );
	}

	/**
	 * Load water bodies' list.
	 * @param {Number[]} waterBodiesfunction 
	 *        An array of objects as exported by Tiled.
	 */
	#addWaterBodies( waterBodies ) { 
		this.#waterBodies = waterBodies; 
		for( let w = waterBodies.length - 1; w >= 0; w = w - 1 ) {
			const direction = waterBodies[ w ].type;
			const match = direction.match(/^(\d+)deg$/);
			let degree = 0;
			if ( match ) {
				degree = match[ 1 ] * 1;
			} else {
				switch( direction ) {
					case "north": degree = 270; break;
					case "east": degree = 0; break;
					case "south": degree = 90; break;
					case "west": degree = 180; break;
				}
			}
			this.#waterBodiesUV[ w ] = { 
				x: Math.cos( degree *  Math.PI / 180 ), 
				y: Math.sin( degree *  Math.PI / 180 ) 
			};
		}
	}

	/**
	 * Load obstacle list.
	 * @param {Number[]} obstacles
	 *        An array of objects as exported by Tiled.
	 */
	#addObstacles( obstacles ) {
		this.#obstacles = [];
		for( let o = obstacles.length - 1; o >= 0; o = o - 1 ) {
			const obstacle = obstacles[ o ];
			this.#obstacles[ o ] = new Box(
				obstacle.x, obstacle.y, obstacle.width, obstacle.height
			);
		}
		this.#lastObstacleIdx = this.#obstacles.length - 1;
	}

	/**
	 * Load interactive list.
	 * @param {Number[]} interactives
	 *        An array of objects as exported by Tiled.
	 */
	#addInteractives( interactives ) {
		this.#interactives = [];
		for( let i = interactives.length - 1; i >= 0; i = i - 1 ) {
			const interactive = interactives[ i ];
			const styleProperties = interactive.hasOwnProperty( "styleProperties" ) 
				? interactive.styleProperties
				: {}
			;
			const goToProperties = interactive.hasOwnProperty( "goToProperties" ) 
				? interactive.goToProperties
				: {}
			;
			const hintPosition = interactive.hasOwnProperty( "hintPosition" ) 
				? interactive.hintPosition
				: {}
			;
			this.#interactives[ i ] = new Interactive(
				interactive.x, interactive.y, interactive.width, interactive.height,
				interactive.name, interactive.type, interactive.text, 
				styleProperties, goToProperties, hintPosition
			);
			this.#remainingInteractivesIdx.add( i );
		}
		this.#lastInteractiveIdx = this.#interactives.length - 1;
	}

	/**
	 * 
	 * @param {Object} area 
	 * @param {Number} numberOfInsects 
	 * @returns {Object[]}
	 */
	#createInsects( area, numberOfInsects ) {
		const insects = [];
		for( let i = numberOfInsects - 1; i >= 0; i = i - 1 )	{
			const position = { 
				x: Math.random() * area.width + area.x, 
				y: Math.random() * area.height + area.y
			}
			insects[ i ] = { position: position };
		}
		return insects;
	}

	#addInsectAreas( insectAreas ) {
		for( let a = insectAreas.length - 1; a >= 0; a = a - 1 ) {
			const insectArea = insectAreas[ a ];
			const area = {
				x: insectArea.x, y: insectArea.y, 
				width: insectArea.width, height: insectArea.height
			};
			this.#insectAreas[ a ] = {
				area: area,
				insects: this.#createInsects( area, insectArea.quantity )
			};
		}
	}

	/**
	 * Draws "insect" particles in the pre-loaded pertinent areas.
	 * @memberof module:game~Game#
	 * @access   private
	 * @function drawInsects
	 */
	#drawInsects() {
		this.#context.save();
		this.#context.fillStyle = "#ffffff88";
		for( let a = this.#insectAreas.length - 1; a >= 0; a = a - 1 ) {
			const insectArea = this.#insectAreas[ a ];
			const area = insectArea.area;
			const maxX = area.x + area.width;
			const maxY = area.y + area.height;
			for( let i = insectArea.insects.length - 1; i >= 0; i = i - 1 ) {
				const insect = insectArea.insects[ i ];
				const position = insect.position;
				position.x += Math.round( 2 * Math.random() - 1 );
				position.y += Math.round( 2 * Math.random() - 1 );
				if ( position.x < area.x ) { position.x = area.x; }
				if ( position.x > maxX ) { position.x = maxX; }
				if ( position.y < area.y ) { position.y = area.y; }
				if ( position.y > maxY ) { position.y = maxY; }
				this.#context.fillRect( position.x, position.y, 4, 4 );
			}
		}
		this.#context.restore();
	}

	/**
	 * Order `{@link module:game~Game#sprites sprites}` by maximum y.
	 * @memberof module:game~Game#
	 * @access   private
	 * @function updateSpriteOrder
	 */
	#updateSpriteOrder() {
		this.#sprites.sort( function( sprite1, sprite2 ) {
			return ( sprite2.maxY - sprite1.maxY );
		} );
	}

	/**
	 * Add sprite at a given position (x/y coordinates) or tile (i/j coordinates).
	 * @param {Sprite} sprite 
	 * @param {Object} [position]
	 * @param {Object} [options]
	 */
	addSprite( 
		sprite, 
		options = this.#defaultSpriteOptions
	) {

		if ( !(sprite instanceof Sprite) ) { return; }

		options = Object.assign( {}, this.#defaultSpriteOptions, options );

		sprite.context = this.#context;

		sprite.options = options;
		if ( options.alwaysOnTop ) {
			this.#spritesOnTop.push( sprite );
			return;
		}

		options.canMove |= options.isPlayer;

		if ( options.canMove ) {
			this.#movingSpritesStates.push( { 
				lastChangeTimestamp: 0,
				changeInterval: sprite.directionChangeInterval
			} );
			sprite.stateIndex = this.#movingSpritesStates.length - 1;
		}

		this.#sprites.push( sprite );
		this.#updateSpriteOrder();

		if ( !options.isPlayer ) { return; }
		this.#playerSprite = sprite;
		this.#cameraOffset.x = sprite.x - ( this.#canvas.width - sprite.width ) / 2;
		this.#cameraOffset.y = sprite.y - ( this.#canvas.height - sprite.height ) / 2;

	}

	/**
	 * Chane the game's player's sprite image.
	 * @param {Image} image 
	 */
	changePlayerSprite( image ) {
		for( let s = this.#sprites.length - 1; s >= 0; s = s - 1 ) {
			if ( !this.#sprites[ s ].options.isPlayer ) { continue; }
			this.#sprites[ s ].image = image;
		}
	}

	/**
	 * Pause the animation loop.
	 * @param {Boolean} [on=true] `true` to pause, `false` to unpause.
	 */
	pause( on = true ) { 
		this.#isPaused = !!on; 
		this.#pressedKeys = new Set();
		this.#activeKey = null;
		this.#isPressingMouseButton = false;
	}

	/**
	 * Check animation loop state.
	 * @returns {Boolean}
	 */
	isPaused() { return this.#isPaused; }

	/** 
	 * Paint collision boxes. 
	 * @memberof module:game~Game#
	 * @access   private
	 * @function drawCollisions
	 */
	#drawCollisions() {

		this.#context.save();

		this.#context.fillStyle = "rgba( 0, 0, 255, 0.5 )";
		for( let o = this.#obstacles.length - 1; o >= 0; o = o - 1 ) {
			this.#context.fillRect( ...this.#obstacles[ o ].rect );
		}

		this.#context.fillStyle = "rgba( 255, 0, 255, 0.5 )";
		for( let i = this.#interactives.length - 1; i >= 0; i = i - 1 ) {
			this.#context.fillRect( ...this.#interactives[ i ].rect );
		}

		this.#context.fillStyle = "rgba( 0, 255, 0, 0.5 )";
		for( let s = this.#sprites.length - 1; s >= 0; s = s - 1 ) {
			this.#context.fillRect( ...this.#sprites[ s ].absoluteHitBox.rect );
		}

		this.#context.restore();

	}

	/**
	 * Determine the collision objects' indices a box is overlapping.
	 * @memberof module:game~Game#
	 * @access   private
	 * @function determineCollisions
	 * @param    {Box} box 
	 * @param    {Number} [skipSpriteIdx=-1]
	 * @param    {Number} [limit=1]
	 * @returns  {Object}
	 */
	#determineCollisions( box, skipSpriteIdx = -1, limit = 1 ) {

		const collisions = { empty: true, obstacles: [], sprites: [] };

		const numberOfObstacles = this.#obstacles.length;
		const numberOfSprites = this.#sprites.length;

		let collisionCount = ( limit == 0 ) 
			? 0 - ( numberOfObstacles + numberOfSprites )
			: 0
		;

		let indexOffset = numberOfObstacles - 1 - this.#lastObstacleIdx;
		for( let o = numberOfObstacles - 1; o >= 0; o = o - 1 ) {
			let collisionIdx = o - indexOffset;
			if ( indexOffset > o ) { collisionIdx += numberOfObstacles; }
			if ( !box.isColliding( this.#obstacles[ collisionIdx ] ) ) {
				continue;
			}
			collisions.empty = false;
			collisions.obstacles.push( collisionIdx );
			collisionCount++;
			this.#lastObstacleIdx = o;
			if ( collisionCount >= limit ) { break; }
		}
		if ( collisionCount >= limit ) { return collisions; }

		for( let s = this.#sprites.length - 1; s >= 0; s = s - 1 ) {
			if ( s === skipSpriteIdx ) { continue; }
			const sprite = this.#sprites[ s ];
			if ( !box.isColliding( sprite.absoluteHitBox ) ) { continue; }
			collisions.empty = false;
			collisions.sprites.push( s );
			collisionCount++;
			if ( collisionCount >= limit ) { break; }
		}
		
		return collisions; 

	}
	
	/**
	 * Check if a box overlaps any number (up to `limit`) of collision objects.
	 * @memberof module:game~Game#
	 * @access   private
	 * @function checkCollisions
	 * @param    {Object} box 
	 * @param    {Number} [skipSpriteIdx=-1]
	 * @param    {Number} [limit=1]
	 * @returns  {Number}
	 */
	#checkCollisions( box, skipSpriteIdx = -1, limit = 1 ) {

		const numberOfObstacles = this.#obstacles.length;
		const numberOfSprites = this.#sprites.length;
		let indexOffset = 0;
		let isColliding = 0;
		let collisionCount = ( limit == 0 ) 
			? 0 - ( numberOfObstacles + numberOfSprites )
			: 0
		;

		indexOffset = numberOfObstacles - 1 - this.#lastObstacleIdx;
		for( let o = numberOfObstacles - 1; o >= 0; o = o - 1 ) {
			let collisionIdx = o - indexOffset;
			if ( indexOffset > o ) { collisionIdx += numberOfObstacles; }
			isColliding |= box.isColliding( this.#obstacles[ collisionIdx ] )
				? Game.CollisionFlag.OBSTACLE
				: Game.CollisionFlag.NONE
			;
			if ( !isColliding ) { continue; }
			collisionCount++;
			this.#lastObstacleIdx = o;
			if ( collisionCount >= limit ) { break; }
		}
		if ( collisionCount >= limit ) { return isColliding; }

		for( let s = this.#sprites.length - 1; s >= 0; s = s - 1 ) {
			if ( s === skipSpriteIdx ) { continue; }
			const sprite = this.#sprites[ s ];
			isColliding |= Game.CollisionFlag[
				box.isColliding( sprite.absoluteHitBox )
					? [ sprite.options.isPlayer ? "PLAYER" : "SPRITE" ]
					: "NONE"
			]
			;
			if ( !isColliding ) { continue; }
			collisionCount++;
			if ( collisionCount >= limit ) { break; }
		}
		return isColliding; 

	}

	/**
	 * Handle key up/down events.
	 * Updates `{@link module:game~Game#pressedKeys pressedKeys}` and `{@link module:game~Game#activeKey activeKey}`
	 * @memberof module:game~Game#
	 * @access   private
	 * @function onKeyEvent
	 * @param    {Event} event 
	 */
	#onKeyEvent( event ) {
		if ( this.#isPaused ) { return; }
		const action = ( event.type == "keydown" ) ? "add" : "delete";
		const hasKey = this.#pressedKeys.has( event.key );
		if ( 
			   ( action == "add" && hasKey )
			|| ( action == "delete" && !hasKey )
		) {
			return;
		}
		this.#pressedKeys[ action ]( event.key );
		this.#activeKey = null;
		for( this.#activeKey of this.#pressedKeys );
	}

	/**
	 * Fire a `keydown` event for a given `key` that will have been determined
	 * from a pointer event.
	 * @param {String} key 
	 */
	#firePointerKeyDownEvent( key ) {
		this.#pointerKeyEquivalent = key;
		this.#onKeyEvent( new KeyboardEvent( "keydown", { key: key } ) );
	}

	/**
	 * Fire a `keyup` event for a given `key` that will have been determined
	 * from a pointer event.
	 * @param {String} key 
	 */
	#firePointerKeyUpEvent( key ) {
		this.#onKeyEvent( new KeyboardEvent( "keyup", { key: key } ) );
	}

	/**
	 * Converts in-game dimensions to a `Box` relative to the canvas.
	 * @param {Number} x 
	 * @param {Number} y 
	 * @param {Number} width 
	 * @param {Number} height 
	 * @param {Object} [translation=this.#getTranslationFromCameraOffset()]
	 * @returns {Box} 
	 */
	#generateCanvasBox( 
		x, y, width, height, 
		translation = this.#getTranslationFromCameraOffset() 
	) {
		return new Box(
			( x + translation.x ) / this.#canvasDistortion.x,
			( y + translation.y ) / this.#canvasDistortion.y,
			width / this.#canvasDistortion.x,
			height / this.#canvasDistortion.y
		);
	}

	/**
	 * Given a target area and the coordinates of a touch event in said area, 
	 * return what user key press it equates to.
	 * @memberof module:game~Game#
	 * @access   private
	 * @function determineControlKeyFromTouch
	 * @param    {DOMRect} target  The bounding rectangle of the target element.
	 * @param    {Number}  clientX The `x` client (window) coordinate
	 * @param    {Number}  clientY The `y` client (window) coordinate
	 * @returns  {String}  One of `{@link module:game~Game#controls controls}`
	 */
	#determineControlKeyFromTouch( targetRect, clientX, clientY ) {

		const translation = this.#getTranslationFromCameraOffset();

		// Coordinates relative to the target
		const inTargetX = clientX - targetRect.left;
		const inTargetY = clientY - targetRect.top;
		// Create a touch `Box` 
		const touchBox = new Box( inTargetX, inTargetY, 10, 10 );

		// Check for interaction
		for( let interactiveIdx of this.#touchedInteractivesIdx ) {
			const box = this.#interactiveBoxes[ interactiveIdx ];
			const targetBox = this.#generateCanvasBox(
				box.x, box.y, box.width, box.height, translation
			);
			if ( !touchBox.isColliding( targetBox ) ) { continue; }
			return this.#controls.INTERACT;
		}

		// Create player `Box` with measuraments accounting for distortion
		const playerTargetBox = this.#generateCanvasBox(
			this.#playerSprite.x, this.#playerSprite.y, 
			this.#playerSprite.width, this.#playerSprite.height,
			translation
		);

		// Touching over the player box has no associated key
		if ( touchBox.isColliding( playerTargetBox ) ) { return null; }

		// Calculate the player's center
		const playerCenterX = playerTargetBox.x + playerTargetBox.width / 2;
		const playerCenterY = playerTargetBox.y + playerTargetBox.height / 2;

		// Get the touch point relative to the players's center
		const relativeX = inTargetX - playerCenterX;
		const relativeY = inTargetY - playerCenterY;
		// Get the absolute angle of the touch point relative to the player's center
		const touchAngle = Math.abs( Math.atan( relativeY / relativeX ) );

		// Get the angle of the target's diagonal
		const targetAngle = Math.atan( targetRect.height / targetRect.width );

		const isUpDown = targetAngle < touchAngle;
		const touchedKey = this.#controls[ isUpDown 
			? ( ( relativeY < 0 ) ? "UP" : "DOWN" )
			: ( ( relativeX < 0 ) ? "LEFT" : "RIGHT" )
		];

		return touchedKey;

	}

	/**
	 * Handle touch events on the canvas.
	 * @memberof module:game~Game#
	 * @access   private
	 * @function onCanvasTouchEvent
	 * @param    {Event} touchEvent 
	 */
	#onCanvasTouchEvent( touchEvent ) {
		
		touchEvent.preventDefault && touchEvent.preventDefault();

		this.#isUsingTouch = true;

		const touchedKey = this.#determineControlKeyFromTouch(
			touchEvent.target.getBoundingClientRect(),
			touchEvent.changedTouches[ 0 ].clientX,
			touchEvent.changedTouches[ 0 ].clientY
		);

		if ( touchEvent.type === "touchstart" ) {
			this.#firePointerKeyDownEvent( touchedKey );
			return;
		}

		if ( touchEvent.type === "touchend" ) {
			this.#firePointerKeyUpEvent( this.#pointerKeyEquivalent );
			return;
		}

		if (
			   ( touchEvent.type === "touchmove" )
			&& ( touchedKey !== this.#pointerKeyEquivalent )
		) {
			this.#firePointerKeyUpEvent( this.#pointerKeyEquivalent );
			this.#firePointerKeyDownEvent( touchedKey );
		}

	}

	/**
	 * Handle de mouse events on the canvas
	 * @memberof module:game~Game#
	 * @access   private
	 * @function onCanvasMouseEvent
	 * @param {Event} mouseEvent 
	 */
	#onCanvasMouseEvent( mouseEvent ) {

		mouseEvent.preventDefault && mouseEvent.preventDefault();

		if ( this.#isPaused ) { return; }

		if (
			   ( mouseEvent.type === "mouseup" )
			|| ( mouseEvent.type === "mouseleave" )
		) {
			console.log( "firePointerKeyUpEvent", this.#pointerKeyEquivalent );
			this.#isPressingMouseButton = false;
			this.#firePointerKeyUpEvent( this.#pointerKeyEquivalent );
			return;
		}

		const isMouseDown = ( mouseEvent.type === "mousedown" );
		const isMouseDrag = (
			   ( mouseEvent.type === "mousemove" )
			&& this.#isPressingMouseButton
		);

		const pointedKey = ( isMouseDown || isMouseDrag )
			? this.#determineControlKeyFromTouch(
				mouseEvent.target.getBoundingClientRect(),
				mouseEvent.clientX,
				mouseEvent.clientY
			)
			: null
		;

		if ( isMouseDown ) {
			console.log( "firePointerKeyDownEvent", pointedKey );
			this.#isPressingMouseButton = true;
			this.#firePointerKeyDownEvent( pointedKey );
			return;
		}

		if ( isMouseDrag && ( pointedKey !== this.#pointerKeyEquivalent ) ) {
			this.#firePointerKeyUpEvent( this.#pointerKeyEquivalent );
			this.#firePointerKeyDownEvent( pointedKey );
		}

	}

	/**
	 * Simulate pressing or releasing a control key.
	 * @param {*} type 
	 * @param {*} controlKey 
	 */
	fireKeyEvent( type, controlKey ) {
		const key = this.#controls[ controlKey ];
		if ( !key ) { return; }
		window.dispatchEvent( 
			new KeyboardEvent( "key" + type, { key: key } ) 
		);
	}

	/**
	 * Simulate pressing the interaction key.
	 */
	fireInteractionEvent() { this.fireKeyEvent( "down", "INTERACT" ); }

	/**
	 * Set the game's movement speed.
	 * @param {Number} [movementSpeed]
	 */
	setMovementSpeed( movementSpeed = this.#movementSpeed ) {
		const newValue = parseFloat( movementSpeed );
		if ( isNaN( newValue ) ) { return; }
		this.#movementSpeed = newValue;
	}

	/**
	 * Set the player's movement speed factor.
	 * @param {Number} [playerSpeedFactor]
	 */
	setPlayerSpeedFactor( playerSpeedFactor = this.#playerSpeedFactor ) {
		const newValue = parseFloat( playerSpeedFactor );
		if ( isNaN( newValue ) ) { return; }
		this.#playerSpeedFactor = newValue;
	}

	/**
	 * Turn collisions overlay on/off.
	 * @param {Boolean} [on] 
	 */
	showCollisions( on = true ) { this.#showCollisions = !!on; }

	/**
	 * Turns grid overlay on/off.
	 * @param {Boolean} [on] 
	 */
	showGrid( on = true ) { this.#showGrid = !!on; }

	/**
	 * Remove objects from screen.
	 */
	clearScreen() {
		this.#backgroundImage = null;
		this.#foregroundImage = null;
		this.#sprites = [];
		this.#spritesOnTop = [];
		this.#playerSprite = null;
		this.#insectAreas = [];
	}

	async loadData( jSONFilesPaths ) {

		this.#screens = await Game.jSONPreloader( jSONFilesPaths );

		for( let screenKey in this.#screens ) {
	
			const filePaths = {};
		
			const screen = this.#screens[ screenKey ];
		
			// Add paths to screen images
			for( let imageKey in screen.images ) {
				filePaths[ imageKey ] = "./img/" + screen.images[ imageKey ];
			}
		
			// Add paths to screen sprites
			for( let s = screen.sprites.length - 1; s >= 0; s = s - 1 ) {
				const fileRelativePath = screen.sprites[ s ].properties.image;
				filePaths[ fileRelativePath ] = "./img/" + fileRelativePath;
			}
		
			this.#images[ screenKey ] = await Game.imagePreloader( filePaths );
		
		}

	}

	#buildSpriteOptions( spriteObject, movementAreas ) {
		let spriteOptions = {};
		for( let optionName in Sprite.DefaultOptions ) {
			if ( !spriteObject.properties.hasOwnProperty( optionName ) ) { continue; }
			spriteOptions[ optionName ] = spriteObject.properties[ optionName ];
			if ( optionName !== "movementArea" ) { continue; }
			spriteOptions.movementArea = movementAreas[ spriteOptions.movementArea ];
		}
		return spriteOptions;
	}

	#getStoredCharacterImage() {
		if ( !this.#images.main ) { return; }
		const characterNumber = localStorage && localStorage.getItem( "character" );
		if ( !characterNumber ) { return null; }
		return  this.#images.main[ "character" + characterNumber ];
	}
	
	#addSprites( screen, screenImages, movementAreas ) {

		for( let s = screen.sprites.length - 1; s >= 0; s = s - 1 ) {
		
			const spriteObject = screen.sprites[ s ];
			let spriteImage = screenImages[ spriteObject.properties.image ];
			if ( !spriteImage ) { continue; }	
		
			const spriteOptions = this.#buildSpriteOptions( 
				spriteObject, movementAreas 
			);
			
			const isPlayer = ( spriteObject.type === "player" );

			if ( isPlayer ) {
				spriteImage = this.#getStoredCharacterImage() || spriteImage;
				spriteOptions.movementArea = {
					x: 0, width: screenImages.background.width,
					y: 0, height: screenImages.background.height
				};
			}
	
			const sprite = new Sprite( 
				spriteObject.x, spriteObject.y, spriteObject.width, spriteObject.height,
				spriteImage,
				spriteOptions 
			);
		
			this.addSprite( 
				sprite, 
				{
					alwaysOnTop: (
							 spriteObject.properties.hasOwnProperty( "alwaysOnTop" )
						&& spriteObject.properties.alwaysOnTop
					),
					canMove: spriteObject.properties.hasOwnProperty( "movementArea" ),
					isPlayer: isPlayer
				}
			);
		
		}

	}

	/**
	 * Loads the resources for a given screen.
	 * @param {String} screenKey 
	 */
	loadScreen( screenKey ) {

		const screenImages = this.#images[ screenKey ];
		const screen = this.#screens[ screenKey ];
	
		if ( !screenImages || !screen ) { return; }

		const movementAreas = {};
		for( let a = screen.areas.length - 1; a >= 0; a = a - 1 ) {
			const movementArea = screen.areas[ a ];
			movementAreas[ movementArea.name ] = movementArea;
		}

		this.clearScreen();

		this.#setWaterTile( screenImages.waterTile );
		this.#backgroundImage = screenImages.background;
		this.#foregroundImage = screenImages.foreground;
		this.#addWaterBodies( screen.water );
		this.#addInsectAreas( screen.insects )
		this.#addObstacles( screen.obstacles );
		this.#addInteractives( screen.interactives );
		this.#addSprites( screen, screenImages, movementAreas );

	}

	/**
	 * Put the player in a certain screen.
	 * @param {String} screenKey 
	 * @param {Object} position x and y coordinates in the new scene
	 */
	goToScreen( screenKey, position = null ) {

		console.log( "Go to", screenKey, position );
		
		this.#isInTransition = true;

		const afterTransitionInCallback = function() {
			// Load screen assets
			this.loadScreen( screenKey );
			// Move player
			if ( position ) {
				this.#playerSprite.x = position.x;
				this.#playerSprite.y = position.y;
				this.#cameraOffset.x = 0
					+ this.#playerSprite.x 
					- ( this.#canvas.width - this.#playerSprite.width ) / 2
				;
				this.#cameraOffset.y = 0
					+ this.#playerSprite.y 
					- ( this.#canvas.height - this.#playerSprite.height ) / 2
				;
		
			}
			const callback = (
				function() { this.#isInTransition = false; }
			).bind( this );
			// Reveal new screen
			this.#screenTransition = {
				shape: "circle",
				isIn: false,
				start: Date.now(),
				duration: 666,
				callback: callback
			};
		}

		this.#screenTransition = {
			shape: "circle",
			isIn: true,
			start: Date.now(),
			duration: 666,
			callback: afterTransitionInCallback.bind( this )
		};

	}

	/** 
	 * Draw a grid overlay. 
	 * @memberof module:game~Game#
	 * @access   private
	 * @function drawGrid
	 */
	#drawGrid() {
		this.#context.save();
		this.#context.strokeStyle = "rgba(0, 0, 0, 0.5)";
		this.#context.setLineDash( [ 2, 2 ] );
		for ( 
			let x = 0; 
			x <= this.#backgroundImage.width; 
			x += this.#tileSize.width 
		) {
			this.#context.beginPath();
			this.#context.moveTo( x, 0 );
			this.#context.lineTo( x, this.#backgroundImage.height );
			this.#context.stroke();
		}
		for ( 
			let y = 0; 
			y <= this.#backgroundImage.height; 
			y += this.#tileSize.height 
		) {
			this.#context.beginPath();
			this.#context.moveTo( 0, y );
			this.#context.lineTo( this.#backgroundImage.width, y );
			this.#context.stroke();
		}
		this.#context.restore();
	}

	/**
	 * Set control keys.
	 * @example <caption>Proper old-school controls</caption>
	 * game.setControls( { LEFT: "o", RIGHT: "p", UP: "q", DOWN: "a" } );
	 * @param {Object} controls 
	 */
	setControls( controls = this.#controls ) {
		for ( let direction in this.#controls ) {
			if ( !controls.hasOwnProperty( direction ) ) { continue; }
			this.#controls[ direction ] = controls[ direction ];
		}
	}

	/**
	 * Calculates how much movement should be applied. 
	 * @memberof module:game~Game#
	 * @access   private
	 * @function getMovementDelta
	 * @param    {String} [pressedKey=this.#activeKey]
	 * @param    {Number} [speedFactor=this.#playerSpeedFactor]
	 * @returns  {Object}
	 */
	#getMovementDelta( 
		pressedKey = this.#activeKey, 
		speedFactor = this.#playerSpeedFactor 
	) {
		const delta = { x: 0, y: 0 };
		switch ( pressedKey ) {
			case this.#controls.UP:
				delta.y = -this.#movementSpeed * this.#canvasDistortion.x * speedFactor; 
			break;
			case this.#controls.LEFT:
				delta.x = -this.#movementSpeed * this.#canvasDistortion.x * speedFactor;
			break;
			case this.#controls.DOWN:
				delta.y = this.#movementSpeed * this.#canvasDistortion.y * speedFactor;
			break;
			case this.#controls.RIGHT: 
				delta.x = this.#movementSpeed * this.#canvasDistortion.y * speedFactor; 
			break;
		}
		return delta;
	}

	/**
	 * Calculate how much to translate the context by, with restrictions.
	 * @memberof module:game~Game#
	 * @access   private
	 * @function getTranslationFromCameraOffset
	 * @returns  {Object}
	 */
	#getTranslationFromCameraOffset() {

		const translation = { x: -this.#cameraOffset.x, y: -this.#cameraOffset.y };
		const minX = this.#canvas.width - this.#backgroundImage.width;
		const minY = this.#canvas.height - this.#backgroundImage.height;

		// Prevent context (camera) from showing outside of map image
		if ( translation.x > 0 ) { translation.x = 0; }
		if ( translation.x < minX ) { translation.x = minX; }
		if ( translation.y > 0 ) { translation.y = 0; }
		if ( translation.y < minY ) { translation.y = minY; }

		return translation;

	}

	/**
	 * Draw an arrow symbol.
	 * @memberof module:game~Game#
	 * @access   private
	 * @function drawArrowSymbol
	 * @param    {Number} x 
	 * @param    {Number} y 
	 * @param    {Number} [size=32]
	 * @param    {Number} [rotationDegrees=0]
	 */
	#drawArrowSymbol( x, y, size = 20, rotationDegrees = 0 ) {

		const fourthOfSize = size / 4;
		const threeFourthsOfSize = 3 * fourthOfSize;
		const halfSize = size / 2;

		this.#context.save();

		if ( rotationDegrees !== 0 ) {
			this.#context.translate( x + halfSize, y + halfSize );
			this.#context.rotate(rotationDegrees * Math.PI / 180 );
			this.#context.translate( -x - halfSize, -y - halfSize );
		}

		this.#context.beginPath();

		this.#context.moveTo( x, y + fourthOfSize );
		this.#context.lineTo( x + halfSize,  y + fourthOfSize );
		this.#context.lineTo( x + halfSize, y );
		this.#context.lineTo( x + size, y + halfSize );
		this.#context.lineTo( x + halfSize, y + size );
		this.#context.lineTo( x + halfSize,  y + threeFourthsOfSize );
		this.#context.lineTo( x,  y + threeFourthsOfSize );
		this.#context.closePath();

		this.#context.fillStyle = "#fff";

		this.#context.fill();

		this.#context.restore();

	}

	/**
	 * Draw a text in a rectangle.
	 * @memberof module:game~Game#
   * @access   private
	 * @function drawTextBox
	 * @param    {String[]} lines
	 * @param    {Object}   anchor Expects `x`, `y` and `position` ("center" or "topleft").
	 * @param    {Object}   styleOptions @see {@link module:game~Interactive.defaultStyleProperties}
	 */
	#drawTextBox( 
		lines, 
		anchor = { 
			x: this.#canvas.width / 2, 
			y: this.#canvas.height / 2,
			position: "topleft"
		},
		styleOptions = {
			font: "24px 'Retro Gaming', monospace",
			textAlign: "left",
			textBaseline: "top",
			textColour: "#fff",
			backgroundColour: "rgba(0, 0, 0, 0.666)",
			borderColour: "#fff"
		}
	) {

		this.#context.save();

		this.#context.font = styleOptions.font;
		this.#context.textAlign = styleOptions.textAlign;
		this.#context.textBaseline = styleOptions.textBaseline;

		// These could be params, too
		const horizontalPadding = 20;
		const verticalPadding = 10;
		const borderWidth = 2;

		const numberOfLines = lines.length;

		let maxLineWidth = 0;
		let maxLineHeight = 0;

		// The rotations of the arrow at the beginning of each line. 
		// `null` value if there is no arrow at the beginning of the line.
		const lineArrowRotations = [];

		for( let l = numberOfLines - 1; l >= 0; l = l - 1 ) {
			let line = lines[ l ];
			// Check for arrow at the beginning of the line
			const firstChar = lines[ l ][ 0 ];
			let arrowRotation = null;
			     if ( firstChar === "➡" ) { arrowRotation = 0; }
			else if ( firstChar === "⬇" ) { arrowRotation = 90; }
			else if ( firstChar === "⬅" ) { arrowRotation = 180; }
			else if ( firstChar === "⬆" ) { arrowRotation = 270; }
			lineArrowRotations[ l ] = arrowRotation;
			if ( arrowRotation !== null ) {
				// Replace arrow char with space for drawn arrow symbol
				lines[ l ] = "  " + line.substring( 1 );
				// Use placeholder same-width chars to calculate line width 
				line = "--" + line.substring( 1 );
			}
			// Calculate line width and approximate line height
			const textMetrics = this.#context.measureText( line );
			const lineWidth = 0 
				+ textMetrics.actualBoundingBoxLeft 
				+ textMetrics.actualBoundingBoxRight
			;
			const lineHeight = 2 * ( 0
				+ textMetrics.actualBoundingBoxAscent 
				+ textMetrics.actualBoundingBoxDescent
			);
			maxLineWidth = ( lineWidth > maxLineWidth ) ? lineWidth : maxLineWidth;
			maxLineHeight = ( lineHeight > maxLineHeight ) 
				? lineHeight 
				: maxLineHeight
			;
		}

		// Calculate box dimensions and position
		const textHeight = maxLineHeight * numberOfLines;
		const halfBorderWidth = borderWidth / 2;
		const paddingWidth = 2 * horizontalPadding;
		const paddingHeight = 2 * verticalPadding;
		const boxWidth = maxLineWidth + paddingWidth;
		const boxHeight = textHeight + paddingHeight;
		let topLeftX = anchor.x;
		let topLeftY = anchor.y;
		if ( anchor.position === "center" ) {
			topLeftX = anchor.x - maxLineWidth / 2 - horizontalPadding;
			topLeftY = anchor.y - textHeight / 2 - verticalPadding;
		}

		// Define outline box
		const box = new Box(
			topLeftX - halfBorderWidth,
			topLeftY - halfBorderWidth,
			boxWidth + borderWidth,
			boxHeight + borderWidth
		);

		// Draw box' border
		this.#context.strokeStyle = styleOptions.borderColour;
		this.#context.lineWidth = borderWidth;
		this.#context.strokeRect( box.x, box.y, box.width, box.height );
		// Draw box' background
		this.#context.fillStyle = styleOptions.backgroundColour;
		this.#context.fillRect( topLeftX, topLeftY, boxWidth, boxHeight );
		// Draw box' text
		this.#context.fillStyle = styleOptions.textColour;
		let lineAdjust = 0;
		if ( styleOptions.textAlign === "center" ) {
			lineAdjust = maxLineWidth / 2;
		}
		for( let l = numberOfLines - 1; l >= 0; l = l - 1 ) {
			const lineX = topLeftX + horizontalPadding + lineAdjust;
			const lineY = topLeftY + paddingHeight + l * maxLineHeight;
			if ( lineArrowRotations[ l ] !== null ) {
				this.#drawArrowSymbol( 
					lineX, lineY, maxLineHeight / 2.75, lineArrowRotations[ l ] 
				);
			}
			this.#context.fillText( lines[ l ], lineX, lineY );
		}

		this.#context.restore();

		return box;

	}

	/**
	 * Draw a triangle over the interactive rectangle.
	 * @memberof module:game~Game#
   * @access   private
	 * @function drawInteractiveHint
	 * @param    {Object} interactive 
	 * @param    {Number} timestamp
	 */
	#drawInteractiveHint( interactive, timestamp ) {

		const position = {
			x: interactive.hintPosition.x,
			y: interactive.hintPosition.y + 10 * Math.sin( timestamp / 100 )
		};

		this.#context.save();
		this.#context.fillStyle = "#ffd700aa";
		this.#context.strokeStyle = "#0008";
		this.#context.strokeWidth = 2;

		/*/
		this.#context.fillRect( position.x - 10, position.y - 16, 20, 4 );
		this.#context.fillRect( position.x - 8, position.y - 12, 16, 4 );
		this.#context.fillRect( position.x - 6, position.y - 8, 12, 4 );
		this.#context.fillRect( position.x - 4, position.y - 4, 8, 4 );
		this.#context.fillRect( position.x - 2, position.y, 4, 4 );
		/*/
		const blockSize = 4;
		const halfBlockSize = blockSize / 2;
		const heightInBlocks = 5;
		const heightLimit = heightInBlocks - 1;
		this.#context.beginPath();
		this.#context.moveTo( 
			position.x - heightLimit * blockSize - halfBlockSize, 
			position.y - blockSize * heightInBlocks - halfBlockSize
		);
		for( let h = heightLimit; h >= 0; h = h - 1 ) {
			const x = position.x - h * blockSize - halfBlockSize;
			const y = position.y - h * blockSize;
			this.#context.lineTo( x, y );
			this.#context.lineTo( x + blockSize, y );
		}
		for( let h = 0; h < heightInBlocks; h = h + 1 ) {
			const x = position.x + h * blockSize - halfBlockSize;
			const y = position.y - h * blockSize;
			this.#context.lineTo( x, y );
			this.#context.lineTo( x + blockSize, y );
		}
		this.#context.lineTo( 
			position.x + heightLimit * blockSize + halfBlockSize, 
			position.y - blockSize * heightInBlocks - halfBlockSize
		);
		this.#context.closePath();
		this.#context.fill();
		this.#context.stroke();
		/**/

		this.#context.restore();

	}

	/**
	 * Draw a pop-up text in a rectangle over a given interactive element.
	 * @memberof module:game~Game#
   * @access   private
	 * @function drawInteractivePopUp
	 * @param    {Object} interactive 
	 */
	#drawInteractivePopUp( interactive ) {
		return this.#drawTextBox( 
			interactive.text.split( "\n" ), 
			{
				x: interactive.x + interactive.width / 2,
				y: interactive.y - interactive.height / 3,
				position: "center"
			},
			interactive.styleProperties
		);
	}

	/**
	 * Check if a (player) box is interacting with an interactive element.
	 * @memberof module:game~Game#
   * @access   private
	 * @function checkInteractions
	 * @param    {Object} box 
	 */
	#checkInteractions( box ) {

		this.#touchedInteractivesIdx.clear();

		if ( this.#isInTransition ) { return; }

		const numberOfInteractives = this.#interactives.length;
		let indexOffset = numberOfInteractives - 1 - this.#lastInteractiveIdx;

		let hintInteraction = false;

		for( let i = numberOfInteractives - 1; i >= 0; i = i - 1 ) {

			let collisionIndex = i - indexOffset;
			if ( indexOffset > i ) { collisionIndex += numberOfInteractives; }

			const interactive = this.#interactives[ i ];

			if ( !box.isColliding( interactive ) ) { continue; }

			this.#touchedInteractivesIdx.add( i );
			this.#remainingInteractivesIdx.delete( i );

			if ( interactive.type === "navigation" ) { continue; }

			if ( this.#activeKey !== this.#controls.INTERACT ) {
				hintInteraction = true;
				continue;
			}

			const eventDetail = { name: interactive.name, type: interactive.type };
			if ( interactive.hasOwnProperty( "goToProperties" ) ) {
				eventDetail.goTo = interactive.goToProperties;
			}

			this.#canvas.dispatchEvent(
				new CustomEvent( 
					"interaction", { bubbles: false, detail: eventDetail } 
				)
			);

			break;

		}

		if ( hintInteraction && !this.#isHintingInteraction ) {
			this.#canvas.dispatchEvent(
				new CustomEvent( "interactionenter", { bubbles: false } )
			);
			this.#isHintingInteraction = true;
			return;
		}

		if ( !hintInteraction && this.#isHintingInteraction ) {
			this.#canvas.dispatchEvent(
				new CustomEvent( "interactionleave", { bubbles: false } )
			);
			this.#isHintingInteraction = false;
			return;
		}


	}
	
	/**
	 * Get the current viewbox coordinatess over the background image.
	 * @memberof module:game~Game#
   * @access   private
	 * @function getViewBox
	 * @returns  {Object}
	 */
	#getViewBox() {
		const viewBox = new Box(
			( this.#cameraOffset.x < 0 ) ? 0 : this.#cameraOffset.x,
			( this.#cameraOffset.y < 0 ) ? 0 : this.#cameraOffset.y,
			this.#canvas.width,
			this.#canvas.height
		);
		if ( viewBox.maxX > this.#backgroundImage.width ) { 
			viewBox.maxX = this.#backgroundImage.width;
		}
		if ( viewBox.maxY > this.#backgroundImage.height ) { 
			viewBox.maxY = this.#backgroundImage.height;
		}
		return viewBox;
	}

	/**
	 * Move and animate sprites.
	 * @memberof module:game~Game#
   * @access   private
	 * @function updateSprites
	 * @param    {Number}  timestamp 
	 * @param    {Object}  [delta] 
	 * @returns  {Boolean} True if player has collided with something
	 */
	#updateSprites( timestamp, delta = this.#getMovementDelta() ) {
		
		const playerIsMoving = ( this.#activeKey !== "" );
		
		this.#updateSpriteOrder(); // TODO: Optimise

		let playerHasCollided = false;
		for( let s = this.#sprites.length - 1; s >= 0; s = s - 1 ) {

			const sprite = this.#sprites[ s ];

			if ( sprite.options.isPlayer && playerIsMoving ) {
				let collisionFlags = false;
				// Move player, flagging it is it has reached outside the movable area
				collisionFlags |= sprite.move( delta, timestamp );
				// Handle interactives
				this.#checkInteractions( sprite );
				// Draw in new position, if there are no collisions
				collisionFlags |= this.#checkCollisions( sprite, s );
				if ( !collisionFlags ) { continue; }
				// If it has collided, revert last move before drawing
				sprite.x -= delta.x;
				sprite.y -= delta.y;
				playerHasCollided = true;
				continue;
			}

			// Non-moving sprites
			if ( !sprite.options.canMove ) {
				sprite.cycleFrames( timestamp ); 
				continue;
			}

			// Moving sprites
			let spriteDirection = sprite.direction;

			const state = this.#movingSpritesStates[ sprite.stateIndex ];
			if ( timestamp > state.lastChangeTimestamp + state.changeInterval ) {
				state.lastChangeTimestamp = timestamp;
				spriteDirection = Math.floor( 5 * Math.random() );
			}

			let pressedKey = "";
			let speedFactor = 0.25;
			switch ( spriteDirection ) {
				case 0: pressedKey = this.#controls.DOWN; break;
				case 1: pressedKey = this.#controls.LEFT; break;
				case 2: pressedKey = this.#controls.RIGHT; break;
				case 3: pressedKey = this.#controls.UP; break;
				case 4: speedFactor = 0; break;
			}
			const spriteDelta = this.#getMovementDelta( pressedKey, speedFactor );

			const hasReachedAreaLimit = Game.checkFlags( 
				sprite.move( spriteDelta, timestamp, this.#movementSpeed ),
				Game.CollisionFlag.OBSTACLE
			);

			const collisions = this.#determineCollisions( sprite, s );

			if ( hasReachedAreaLimit || !collisions.empty ) {
				sprite.x -= spriteDelta.x;
				sprite.y -= spriteDelta.y;
				if ( hasReachedAreaLimit || ( collisions.obstacles.length > 0 ) ) {

					sprite.direction = sprite.leastUsedDirection;

				} else if ( collisions.sprites.length > 0 ) {

					const collidedSprite = this.#sprites[ collisions.sprites[ 0 ] ];

					// Walk away from other sprites that are not the player
					if ( !collidedSprite.options.isPlayer ) {
						
						sprite.turnAround();

						// Also, if the other sprite can move, change its direction
						if ( collidedSprite.options.canMove ) {
							collidedSprite.turnAround();
						}

					}

				}
			}

		}

		return playerHasCollided;

	}

	/**
	 * Draw all the sprites.
	 * @memberof module:game~Game#
   * @access   private
	 * @function drawSprites
	 * @param    {Number} timestamp 
	 */
	#drawSprites( timestamp ) {
		const viewBox = this.#getViewBox();
		for( let s = this.#sprites.length - 1; s >= 0; s = s - 1 ) {
			const sprite = this.#sprites[ s ];
			if ( !sprite.isColliding( viewBox ) ) { continue; }
			sprite.draw( timestamp );
		}
	}

	/**
	 * Draw sprites that are meant to be shown above the others, below the 
	 * foreground image.
	 * @memberof module:game~Game#
   * @access   private
	 * @function drawTopSprites
	 * @param    {Number} timestamp 
	 */
	#drawTopSprites( timestamp ) {
		for( let s = this.#spritesOnTop.length - 1; s >= 0; s = s - 1 ) {
			const sprite = this.#spritesOnTop[ s ];
			const speed = this.#movementSpeed;
			sprite.cycleFrames( timestamp, speed ); 
			sprite.draw( timestamp ); 
		}
	}

	/**
	 * Draw rectangles filled the water pattern, sliding in a certain direction.
	 * @memberof module:game~Game#
   * @access   private
	 * @function drawSlidingWaterBodies
	 * @param    {Number} timestamp 
	 */
	#drawSlidingWaterBodies( timestamp ) {
		
		const currentSecond = timestamp / 1000;
		const speed = 8 * this.#movementSpeed;
		const increment = speed * currentSecond;

		const tileWithx2 = this.#waterTileImage.width * 2;
		const tileHeightx2 = this.#waterTileImage.height * 2;

		this.#context.fillStyle = this.#waterPattern;
		for( let w = this.#waterBodies.length - 1; w >= 0; w = w - 1 ) {

			const unitVector = this.#waterBodiesUV[ w ];
			const displacementX = 0
				+ ( increment * unitVector.x ) % this.#waterTileImage.width
				- this.#waterTileImage.width
			;
			const displacementY = 0
				+ ( increment * unitVector.y ) % this.#waterTileImage.height
				- this.#waterTileImage.height
			;

			const waterBody = this.#waterBodies[ w ];

			this.#context.save();
			this.#context.translate( displacementX, displacementY );
			this.#context.fillRect(
				waterBody.x, waterBody.y, 
				waterBody.width + tileWithx2, waterBody.height + tileHeightx2
			);
			this.#context.restore();

		}
		
	}

	/**
	 * Draw rectangles filled the water pattern, rotating periodically.
	 * @memberof module:game~Game#
   * @access   private
	 * @function drawRotatingWaterBodies
	 * @param    {Number} timestamp 
	 */
	#drawRotatingWaterBodies( timestamp ) {
		const currentSecond = timestamp / 1000;
		const frame = Math.round( ( currentSecond / 5 ) % 4 );
		const matrix = new DOMMatrix();
		this.#waterPattern.setTransform( matrix.rotate( 90 * frame ) );
		for( let w = this.#waterBodies.length - 1; w >= 0; w = w - 1 ) {
			const waterBody = this.#waterBodies[ w ];
			this.#context.save();
			this.#context.fillStyle = this.#waterPattern;
			this.#context.fillRect(
				waterBody.x, waterBody.y, 
				waterBody.width, waterBody.height
			);
			this.#context.restore();
		}
	}

	/**
	 * Draw rectangles filled with a watter effect.
	 * @memberof module:game~Game#
   * @access   private
	 * @function drawWaterBodies
	 * @param    {Number} timestamp 
	 * @param    {String} [mode=sliding]
	 */
	#drawWaterBodies( timestamp, mode = "sliding" ) {

		if ( !this.#waterTileImage || !this.#waterPattern ) { return; }

		if ( mode === "sliding" ) { this.#drawSlidingWaterBodies( timestamp ); }
		if ( mode === "rotating" ) { this.#drawRotatingWaterBodies( timestamp ); }
	}

	/**
	 * Get the longest `x` and `y` measurements from the player's center to the
	 * canvas' corners.
	 * @returns {Object}
	 */
	#getPlayerFurthestCornerMeasures() {
		const furthestPoint = {
			x: this.#playerSprite.x + this.#playerSprite.width / 2,
			y: this.#playerSprite.y + this.#playerSprite.height / 2
		};
		if ( furthestPoint.x < this.#canvasWidth / 2 ) {
			furthestPoint.x = this.#canvasWidth - furthestPoint.x;
		}
		if ( furthestPoint.y < this.#canvasHeight / 2 ) {
			furthestPoint.y = this.#canvasHeight - furthestPoint.y;
		}
		return furthestPoint;
	}

	/**
	 * Add a closed arc to the context, centered on the player's box center
	 * and extending a `perunit` proportion of the longest `x` and `y` 
	 * measurements from the player's center to the canvas' corners.
	 * @param {Number} perunit 
	 */
	#addCircleAroundPlayerCenter( perunit ) {
		const furthestPoint = this.#getPlayerFurthestCornerMeasures();
		const distance = Math.sqrt( 
				Math.pow( furthestPoint.x, 2 ) 
			+ Math.pow( furthestPoint.y, 2 ) 
		);
		this.#context.arc(
			this.#playerSprite.x + this.#playerSprite.width / 2, 
			this.#playerSprite.y + this.#playerSprite.height / 2, 
			Game.#lerp( 0, distance, perunit ), 
			0, 2 * Math.PI
		);
	}

	/**
	 * Add the screen transition effect to the context, if there is one set.
	 */
	#executeScreenTransitionEffect() {

		if ( !this.#screenTransition ) { return; }

		let hasFinished = false;

		const elapsedMiliseconds = Date.now() - this.#screenTransition.start; 
		let perunit = elapsedMiliseconds / this.#screenTransition.duration;
		if ( perunit >= 1 ) { 
			hasFinished = true;
			perunit = 1; 
		}

		if ( this.#screenTransition.isIn ) { perunit = 1 - perunit; }
		this.#context.beginPath();
		if ( this.#screenTransition.shape == "circle" ) {
			this.#addCircleAroundPlayerCenter( perunit )
		}
		this.#context.closePath();
		this.#context.clip();

		if ( hasFinished && ( this.#screenTransition.callback ) ) {
			this.#screenTransition.callback();
		}

	}

	/**
	 * Draw a single frame.
	 * @param {Number} [timestamp=0]
	 */
	drawFrame( timestamp = 0 ) {

		this.#canvas.width = this.#canvasWidth;
		this.#canvas.height = this.#canvasHeight;

		// Add a dark background
		this.#context.fillStyle = "#202";
		this.#context.fillRect( 
			0, 0, this.#canvasWidth, this.#canvasHeight
		);

		const movementDelta = this.#getMovementDelta();

		this.#cameraOffset.x += movementDelta.x;
		this.#cameraOffset.y += movementDelta.y;

		const translation = this.#getTranslationFromCameraOffset();

		this.#context.save();
		this.#context.translate( translation.x, translation.y );

		this.#executeScreenTransitionEffect();

		// Add water colour
		this.#context.fillStyle = "#3372a0";
		this.#context.fillRect( 
			0, 0, this.#backgroundImage.width, this.#backgroundImage.height
		);
		this.#drawWaterBodies( timestamp );
		
		this.#context.drawImage( this.#backgroundImage, 0, 0 );
		this.#drawInsects();
		const playerHasCollided = this.#updateSprites( timestamp, movementDelta );
		if ( playerHasCollided ) {
			this.#cameraOffset.x -= movementDelta.x;
			this.#cameraOffset.y -= movementDelta.y;
		}
		this.#drawSprites( timestamp );
		this.#drawTopSprites( timestamp );
		if ( this.#foregroundImage ) {
			this.#context.drawImage( this.#foregroundImage, 0, 0 );
		}
		if ( this.#showCollisions ) { this.#drawCollisions(); }
		if ( this.#showGrid ) { this.#drawGrid(); }
				
		let interactiveIdx = 0;
		for( 
			interactiveIdx = this.#interactives.length - 1; 
			interactiveIdx >= 0; 
			interactiveIdx = interactiveIdx - 1
		) {
			const interactive = this.#interactives[ interactiveIdx ];
			if ( !this.#touchedInteractivesIdx.has( interactiveIdx ) ) { 
				if (
					   ( interactive.type === "navigation" )
					&& this.#remainingInteractivesIdx.has( interactiveIdx )
				) {
					this.#drawInteractiveHint( 
						this.#interactives[ interactiveIdx ], timestamp
					);
				}
				continue;
			}
			if ( ( interactive.type !== "navigation" ) && !interactive.text ) {
				interactive.text = ""
					+ ( this.#isUsingTouch ? "Touch" : "Click" ) + " here\n"
					+ "or press \"" + this.#controls.INTERACT + "\""
				;
			}
			const box = this.#drawInteractivePopUp( interactive );
			this.#interactiveBoxes[ interactiveIdx ] = box;
		}
		this.#lastInteractiveIdx = interactiveIdx;

		this.#context.restore();

	}

	/**
	 * Handle the animation loop.
	 * @memberof module:game~Game#
	 * @access   private
	 * @function requestAnimationFrame
	 * @param    {Number} timestamp 
	 */
	#requestAnimationFrame( timestamp ) {

		if ( this.#isPaused ) { 
			this.#animationTimeSpan = 0;
			window.requestAnimationFrame( this.#requestAnimationFrame.bind( this ) );
			return; 
		}

		const timeDelta = timestamp - this.#lastTimestamp;
		this.#animationTimeSpan += timeDelta;
		this.#lastTimestamp = timestamp;
		if (
			   this.#animationTimeSpan
			&& ( this.#animationTimeSpan < this.#animationInterval )
		) {
			this.#currentFrame += 1;
			window.requestAnimationFrame( this.#requestAnimationFrame.bind( this ) );
			return;
		}

		this.#currentFrame += 1;
		// if ( this.#currentFrame == this.#fps ) { this.#currentFrame = 0; }

		this.drawFrame( timestamp );

		window.requestAnimationFrame( this.#requestAnimationFrame.bind( this ) );

	}

	/**
	 * Create a 2D game.
	 * @param {HTMLCanvasElement} canvas 
	 * @param {Object}            tileSize
	 * @param {Boolean}           desynchronized
	 */
	constructor( canvas, tileSize = this.#tileSize, desynchronized = false ) {
		
		if ( !( canvas instanceof HTMLCanvasElement ) ) { 
			console.error( "Game requires a canvas element" );
			return; 
		}
		this.#canvas = canvas;

		this.#boundingClientRect = this.#canvas.getBoundingClientRect();
		if ( !( this.#boundingClientRect instanceof DOMRect ) ) { return; }
		this.#canvasWidth = this.#boundingClientRect.width;
		this.#canvasHeight = this.#boundingClientRect.height;
		const canvasRatio = this.#canvasWidth / this.#canvasHeight;
		if ( 
			   ( canvasRatio >= 1 ) 
			&& ( this.#canvasWidth < this.#minCanvasDimensions.width )
		) { 
			this.#canvasWidth = this.#minCanvasDimensions.width;
			this.#canvasHeight = this.#canvasWidth / canvasRatio;
		}
		if ( 
					( canvasRatio < 1 ) 
			&& ( this.#canvasHeight < this.#minCanvasDimensions.height )
		) { 
			this.#canvasHeight = this.#minCanvasDimensions.height;
			this.#canvasWidth = this.#canvasHeight * canvasRatio;
		}

		this.#canvas.width = this.#canvasWidth;
		this.#canvas.height = this.#canvasHeight;
		this.#canvasDistortion = {
			x: this.#canvasWidth / this.#boundingClientRect.width,
			y: this.#canvasHeight / this.#boundingClientRect.height
		};

		const contextAttributes = { 
			alpha: false, 
			desynchronized: desynchronized
		};
		this.#context = this.#canvas.getContext( "2d", contextAttributes );

		if ( !( this.#context instanceof CanvasRenderingContext2D ) ) { return; }

		this.#context.fillStyle = "#ffffff";
		this.#context.fillRect( 0, 0, this.#canvas.width, this.#canvas.height );

		this.#tileSize = tileSize;

		const onKeyEvent = this.#onKeyEvent.bind( this );
		window.addEventListener( "keyup", onKeyEvent );
		window.addEventListener( "keydown", onKeyEvent );

		const onCanvasTouchEvent = this.#onCanvasTouchEvent.bind( this );
		canvas.addEventListener( "touchstart", onCanvasTouchEvent );
		canvas.addEventListener( "touchmove", onCanvasTouchEvent );
		canvas.addEventListener( "touchend", onCanvasTouchEvent );
		const onCanvasMouseEvent = this.#onCanvasMouseEvent.bind( this );
		canvas.addEventListener( "mousedown", onCanvasMouseEvent );
		canvas.addEventListener( "mousemove", onCanvasMouseEvent );
		canvas.addEventListener( "mouseup", onCanvasMouseEvent );
		canvas.addEventListener( "mouseleave", onCanvasMouseEvent );

		window.requestAnimationFrame( this.#requestAnimationFrame.bind( this ) );

	}

}

export { Game }